import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideEyeOff, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LogoComponent } from '../../components/logo/logo.component';
import { AccountClaimInfo, AuthService } from '../../services/auth.service';
import {
  PARTNER_PASSWORD_MIN_LENGTH,
  passwordsMatch,
} from '../partner-set-password/partner-set-password.component';

/**
 * /claim?token=… from the mail "<Agentur> hat dein Schenkwerk-Konto angelegt": the
 * customer sets an own password, accepts the AGB personally and is logged in. The
 * agency keeps working in the account until the customer ends its access.
 */
@Component({
  selector: 'app-claim-account',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmCheckboxImports,
    HlmFieldImports,
    HlmInputGroupImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    LogoComponent,
  ],
  providers: [provideIcons({ lucideEye, lucideEyeOff, lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './claim-account.component.html',
})
export class ClaimAccountComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly token = inject(ActivatedRoute).snapshot.queryParamMap.get('token');
  readonly minLength = PARTNER_PASSWORD_MIN_LENGTH;
  readonly info = signal<AccountClaimInfo | null>(null);
  readonly loadingInfo = signal(true);
  /** The link can't be used (any more): the page offers "Passwort vergessen" instead. */
  readonly linkError = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly loading = signal(false);
  readonly submitted = signal(false);
  readonly showPassword = signal(false);

  readonly form = inject(FormBuilder).nonNullable.group(
    {
      password: ['', [Validators.required, Validators.minLength(PARTNER_PASSWORD_MIN_LENGTH)]],
      confirm: ['', Validators.required],
      acceptTerms: [false, Validators.requiredTrue],
    },
    { validators: passwordsMatch },
  );

  constructor() {
    if (!this.token) {
      this.linkError.set('Der Link ist unvollständig.');
      this.loadingInfo.set(false);
      return;
    }
    this.authService.claimInfo(this.token).subscribe({
      next: (info) => {
        this.info.set(info);
        this.loadingInfo.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.linkError.set(
          err.status === 400
            ? (err.error?.detail ?? 'Der Link ist ungültig oder abgelaufen.')
            : 'Der Link konnte nicht geprüft werden. Bitte lade die Seite neu.',
        );
        this.loadingInfo.set(false);
      },
    });
  }

  invalid(field: 'password' | 'confirm' | 'acceptTerms'): boolean {
    if (!this.submitted()) return false;
    if (field === 'confirm') {
      return this.form.controls.confirm.invalid || !!this.form.errors?.['passwordsNotMatching'];
    }
    return this.form.controls[field].invalid;
  }

  submit(): void {
    if (this.loading() || !this.token) return;
    this.submitted.set(true);
    this.error.set(null);
    if (this.form.invalid) return;

    this.loading.set(true);
    this.authService.claimAccount(this.token, this.form.getRawValue().password).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/']);
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        if (err.status === 400) {
          this.linkError.set(err.error?.detail ?? 'Der Link ist ungültig oder abgelaufen.');
        } else {
          this.error.set('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
        }
      },
    });
  }
}
